import { redirect } from 'react-router';
import { loadFrom, portalClient, type ApiFailure } from '../api';
import { NEXT_PARAM, ROUTES } from '../app/routes';
import { sessionStore } from './session';
import type { CurrentUser, Organisation } from './store';

/**
 * The loader that guards everything behind sign-in (P0-WEB-07).
 *
 * Two requests, in parallel: `GET /v1/auth/me` says who the session belongs to
 * (P0-PLAT-20), `GET /v1/organisations` says what they may act in. Both are
 * needed before the shell paints — the account menu renders the first, and
 * every subsequent request needs one of the second in `X-Organisation-Id` — so
 * serialising them would cost a round trip for nothing.
 *
 * Either answering 401 means there is no session. That is the whole guard.
 */

export interface SessionData {
  readonly user: CurrentUser | undefined;
  readonly organisations: readonly Organisation[];
  /**
   * Present when the session is fine but the request was not — a 500, or the
   * API being unreachable. The shell renders it and stays put.
   */
  readonly failure: ApiFailure | undefined;
}

/**
 * The in-flight bootstrap, shared by everyone who needs it.
 *
 * React Router runs a parent loader and its children **in parallel**, so a
 * child that needs `X-Organisation-Id` starts before this one has established
 * it. That is not a race the child can win by being careful: the header is
 * absent, `ResolveOrganisation` leaves the tenant unset, and `OrganisationScope`
 * throws rather than quietly returning every tenant's rows — a 500 on the first
 * paint after signing in.
 *
 * So the bootstrap is a promise rather than a step. Whoever asks first performs
 * it; everyone else awaits the same one. Cleared when it settles, so a later
 * navigation re-fetches rather than serving a session from ten minutes ago.
 */
let inFlight: Promise<SessionData> | undefined;

export function bootstrapSession(request: Request): Promise<SessionData> {
  inFlight ??= runBootstrap(request).finally(() => {
    inFlight = undefined;
  });

  return inFlight;
}

/** Tests only — module state leaks between them otherwise. */
export function resetSessionBootstrap(): void {
  inFlight = undefined;
}

export function sessionLoader({ request }: { request: Request }): Promise<SessionData> {
  return bootstrapSession(request);
}

async function runBootstrap(request: Request): Promise<SessionData> {
  const client = portalClient();

  const [me, organisations] = await Promise.all([
    loadFrom(() => client.request('/v1/auth/me', 'get')),
    loadFrom(() => client.request('/v1/organisations', 'get')),
  ]);

  // Checked one at a time rather than as a pair, so each narrows: the compiler
  // cannot see that "at least one failed" leaves the other still possibly ok.
  if (!me.ok) return settle(me.failure, request);
  if (!organisations.ok) return settle(organisations.failure, request);

  sessionStore.authenticated(me.data, organisations.data.data);

  return { user: me.data, organisations: organisations.data.data, failure: undefined };
}

/**
 * What a failed probe means.
 *
 * **Only a 401 signs the user out.** A 500 or a dropped connection says nothing
 * about whether the session is valid, and treating them alike is how a backend
 * deploy logs everybody out — losing unsaved work to a problem that fixed
 * itself in thirty seconds.
 */
function settle(failure: ApiFailure, request: Request): SessionData {
  if (failure.kind === 'unauthenticated') {
    sessionStore.clear();
    // React Router's own control flow: a thrown Response is how a loader
    // navigates, and `redirect()` returns one rather than an Error. The rule is
    // right in general and wrong for exactly this library idiom.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(signInWithReturnTo(request.url));
  }

  return { user: undefined, organisations: [], failure };
}

/**
 * Where to send somebody who needs to sign in first, remembering where they
 * were going.
 *
 * Only the path, never the whole URL: `safeNextPath` rejects anything else on
 * the way back, and putting an absolute URL in the parameter would mean writing
 * a link that looks like an open redirect even though it is refused.
 */
export function signInWithReturnTo(url: string): string {
  const { pathname, search } = new URL(url);
  const next = `${pathname}${search}`;

  // No point remembering the root — it is where sign-in goes by default, and
  // `?next=%2F` in the address bar is noise a user may reasonably distrust.
  if (next === ROUTES.root) return ROUTES.signIn;

  return `${ROUTES.signIn}?${NEXT_PARAM}=${encodeURIComponent(next)}`;
}

/**
 * The inverse guard: somebody already signed in has no use for the sign-in
 * form.
 *
 * Answered from memory rather than by asking the API. A probe here would put an
 * authenticated round trip on the most-visited anonymous page in the product to
 * catch a case that only arises when a signed-in user types the URL by hand —
 * and after a cold load memory says `unknown`, so the form renders and signing
 * in works exactly as it should.
 */
export function redirectIfSignedIn(): Response | null {
  return sessionStore.getSnapshot().status === 'authenticated' ? redirect(ROUTES.root) : null;
}
